import React, { useEffect, useState, useCallback, useRef } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCamera, faCheckCircle, faSackDollar, faPaperclip, faTimesCircle,
  faHourglass, faTimes, faUpload, faBuilding,
  faUniversity, faFileInvoiceDollar, faCoins, faInfoCircle, faRotate,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
  UiModal,
  UiButton,
  StatusBadge,
} from '../../components/ui';
import type { StatCardDef, StatusDef } from '../../components/ui';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = 'Asia/Bangkok';

const FILTER_SELECT_CLASS =
  'w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';
const INPUT_CLASS =
  'w-full px-3.5 py-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';
const TEXTAREA_CLASS = `${INPUT_CLASS} resize-none`;
const LABEL_CLASS =
  'block text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-1.5';

const fmt = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SETTLEMENT_STATUS_MAP: Record<string, StatusDef> = {
  pending:   { bg: 'bg-amber-100',  text: 'text-amber-700',  icon: faHourglass,   label: 'Pending'   },
  completed: { bg: 'bg-green-100',  text: 'text-green-700',  icon: faCheckCircle, label: 'Completed' },
  rejected:  { bg: 'bg-red-100',    text: 'text-red-600',    icon: faTimesCircle, label: 'Rejected'  },
};

interface AmountBreakdownProps {
  thb: number;
  usdtCoins: number;
  usdtThb: number;
  currencyFilter: string;
  accentColor?: string;
}

const AmountBreakdown: React.FC<AmountBreakdownProps> = ({ thb, usdtCoins, usdtThb, currencyFilter, accentColor }) => {
  const showAll = currencyFilter === '';
  const showThb = showAll || currencyFilter === 'THB';
  const showUsdt = showAll || currencyFilter === 'USDT';
  const totalAll = thb + usdtThb;

  if ((showThb && thb === 0) && (showUsdt && usdtCoins === 0)) {
    return <span className="text-sm text-gray-300 dark:text-slate-600">ไม่มีข้อมูล</span>;
  }

  return (
    <div className="space-y-1">
      {showAll && (thb > 0 || usdtCoins > 0) && (
        <p className="text-sm font-bold" style={{ color: accentColor || 'var(--theme-primary)' }}>
          ฿{fmt(totalAll)} ทั้งหมด
        </p>
      )}
      {showThb && thb > 0 && (
        <p className="text-xs text-blue-600 dark:text-blue-400 font-semibold tabular-nums">
          <span className="text-gray-400 dark:text-slate-500 font-normal"></span>฿{fmt(thb)} บาท
        </p>
      )}
      {showUsdt && usdtCoins > 0 && (
        <p className="text-xs text-green-600 dark:text-green-400 font-semibold tabular-nums">
          {usdtCoins.toFixed(2)} USDT
          <span className="text-gray-400 dark:text-slate-500 font-normal ml-1">≈฿{fmt(usdtThb)}</span>
        </p>
      )}
    </div>
  );
};

const SettlementManagementPage: React.FC = () => {
  const [settlements, setSettlements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');

  const [dateFrom, setDateFrom] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [appliedDateFrom, setAppliedDateFrom] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [appliedDateTo, setAppliedDateTo] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));

  const [selectedSettlement, setSelectedSettlement] = useState<any>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [slipFile, setSlipFile] = useState<File | null>(null);
  const [slipPreview, setSlipPreview] = useState('');
  const [note, setNote] = useState('');
  const [showSlipModal, setShowSlipModal] = useState(false);
  const [selectedSlipUrl, setSelectedSlipUrl] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailSettlement, setDetailSettlement] = useState<any>(null);
  const [approveWalletAddress, setApproveWalletAddress] = useState('');
  const [approving, setApproving] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  const [summaryData, setSummaryData] = useState({
    all: { count: 0, thb: 0, usdt_coins: 0, usdt_thb: 0 },
    pending: { count: 0, thb: 0, usdt_coins: 0, usdt_thb: 0 },
    completed: { count: 0, thb: 0, usdt_coins: 0, usdt_thb: 0 },
    rejected: { count: 0, thb: 0, usdt_coins: 0, usdt_thb: 0 },
  });
  const [summaryLoading, setSummaryLoading] = useState(false);

  const loadSettlements = useCallback(async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * pageSize;
      let url = `/admin/settlements?limit=${pageSize}&offset=${offset}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (currencyFilter) url += `&currency=${currencyFilter}`;
      if (appliedDateFrom) url += `&start_date=${appliedDateFrom}`;
      if (appliedDateTo) url += `&end_date=${appliedDateTo}`;
      const response = await merchantApi.get(url);
      setSettlements(response.data.data || []);
      setTotalItems(response.data.pagination?.total || 0);
    } catch {
      toast.error('ไม่สามารถโหลดข้อมูลได้');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, statusFilter, currencyFilter, appliedDateFrom, appliedDateTo]);

  const loadSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);
      let url = `/admin/settlements/summary?`;
      if (currencyFilter) url += `&currency=${currencyFilter}`;
      if (appliedDateFrom) url += `&start_date=${appliedDateFrom}`;
      if (appliedDateTo) url += `&end_date=${appliedDateTo}`;
      const response = await merchantApi.get(url);
      if (response.data.success) setSummaryData(response.data.data);
    } catch { /* silent */ }
    finally { setSummaryLoading(false); }
  }, [currencyFilter, appliedDateFrom, appliedDateTo]);

  const prevFiltersRef = useRef({ statusFilter, currencyFilter });

  useEffect(() => {
    const prev = prevFiltersRef.current;
    const filtersChanged = prev.statusFilter !== statusFilter || prev.currencyFilter !== currencyFilter;
    if (filtersChanged) {
      prevFiltersRef.current = { statusFilter, currencyFilter };
      if (currentPage !== 1) { setCurrentPage(1); return; }
    }
    loadSettlements();
  }, [loadSettlements, currentPage, statusFilter, currencyFilter, appliedDateFrom, appliedDateTo]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const handleSearch = () => {
    if (dayjs(dateFrom).isAfter(dayjs(dateTo), 'day')) {
      toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด');
      return;
    }
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setCurrentPage(1);
  };

  const handleReset = () => {
    const today = dayjs().tz(TZ).format('YYYY-MM-DD');
    setDateFrom(today);
    setDateTo(today);
    setAppliedDateFrom(today);
    setAppliedDateTo(today);
    setStatusFilter('');
    setCurrencyFilter('');
    setCurrentPage(1);
  };

  const formatSlipUrl = (slipUrl: string) => {
    if (!slipUrl) return '';
    if (!slipUrl.startsWith('http') && !slipUrl.startsWith('data:')) {
      return `data:image/png;base64,${slipUrl}`;
    }
    return slipUrl;
  };

  const handleViewSlip = (slipUrl: string) => {
    setSelectedSlipUrl(formatSlipUrl(slipUrl));
    setShowSlipModal(true);
  };

  const handleSlipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSlipFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setSlipPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const closeApproveModal = () => {
    setShowApproveModal(false);
    setSelectedSettlement(null);
    setSlipFile(null);
    setSlipPreview('');
    setNote('');
    setApproveWalletAddress('');
  };

  const handleApprove = async () => {
    if (!slipPreview) {
      toast.error('กรุณาอัพโหลดสลิป');
      return;
    }
    const needsAddress = selectedSettlement.currency === 'USDT' && !selectedSettlement.wallet_address;
    if (needsAddress && !approveWalletAddress.trim()) {
      toast.error('กรุณาระบุ USDT wallet address ก่อน approve');
      return;
    }
    try {
      setApproving(true);
      const payload: any = { slip_url: slipPreview, note };
      if (needsAddress) payload.wallet_address = approveWalletAddress.trim();
      await merchantApi.post(`/admin/settlements/${selectedSettlement.id}/approve`, payload);
      toast.success('อนุมัติการถอนเงินสำเร็จ!');
      closeApproveModal();
      loadSettlements();
      loadSummary();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async (settlement: any) => {
    const reason = window.prompt('เหตุผลในการปฏิเสธ:');
    if (!reason) return;
    try {
      await merchantApi.post(`/admin/settlements/${settlement.id}/reject`, { note: reason });
      toast.success('ปฏิเสธและคืนเงินสำเร็จ!');
      loadSettlements();
      loadSummary();
    } catch {
      toast.error('เกิดข้อผิดพลาด');
    }
  };

  const { all: sumAll, pending: sumPending, completed: sumCompleted, rejected: sumRejected } = summaryData;

  const statCards: StatCardDef[] = [
    {
      icon: faFileInvoiceDollar,
      iconClassName: 'text-white',
      iconStyle: { background: 'var(--theme-primary-gradient)' },
      label: 'ทั้งหมด',
      value: <AmountBreakdown thb={sumAll.thb} usdtCoins={sumAll.usdt_coins} usdtThb={sumAll.usdt_thb} currencyFilter={currencyFilter} />,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">{sumAll.count.toLocaleString()} รายการ</span>,
    },
    {
      icon: faHourglass,
      iconClassName: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      label: 'Pending',
      value: <AmountBreakdown thb={sumPending.thb} usdtCoins={sumPending.usdt_coins} usdtThb={sumPending.usdt_thb} currencyFilter={currencyFilter} accentColor="#d97706" />,
      subValue: <span className="text-xs text-amber-600 dark:text-amber-400">{sumPending.count.toLocaleString()} รายการ</span>,
    },
    {
      icon: faCheckCircle,
      iconClassName: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      label: 'Completed',
      value: <AmountBreakdown thb={sumCompleted.thb} usdtCoins={sumCompleted.usdt_coins} usdtThb={sumCompleted.usdt_thb} currencyFilter={currencyFilter} accentColor="#16a34a" />,
      subValue: <span className="text-xs text-green-600 dark:text-green-400">{sumCompleted.count.toLocaleString()} รายการ</span>,
    },
    {
      icon: faTimesCircle,
      iconClassName: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      label: 'Rejected',
      value: <AmountBreakdown thb={sumRejected.thb} usdtCoins={sumRejected.usdt_coins} usdtThb={sumRejected.usdt_thb} currencyFilter={currencyFilter} accentColor="#dc2626" />,
      subValue: <span className="text-xs text-red-600 dark:text-red-400">{sumRejected.count.toLocaleString()} รายการ</span>,
    },
    {
      icon: faSackDollar,
      iconClassName: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      label: 'รอถอน',
      value: <AmountBreakdown thb={sumPending.thb} usdtCoins={sumPending.usdt_coins} usdtThb={sumPending.usdt_thb} currencyFilter={currencyFilter} />,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">ยอดรอดำเนินการ</span>,
    },
  ];

  const columns: Column<any>[] = [
    {
      key: 'merchant',
      header: 'Merchant',
      width: '200px',
      render: (s) => (
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 flex-shrink-0">
            <FontAwesomeIcon icon={faBuilding} className="text-xs" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-gray-800 dark:text-slate-100 text-sm truncate">{s.merchants?.name || '-'}</p>
            <p className="text-xs text-gray-400 dark:text-slate-500 truncate">{s.merchants?.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'ยอดถอน',
      width: '130px',
      sortable: true,
      render: (s) => (
        <div>
          <p className="font-bold text-sm tabular-nums whitespace-nowrap" style={{ color: 'var(--theme-primary)' }}>
            ฿{parseFloat(s.amount).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          {s.currency === 'USDT' && s.usdt_amount && (
            <p className="text-xs text-green-600 dark:text-green-400 font-semibold mt-0.5 flex items-center gap-1 tabular-nums">
              <FontAwesomeIcon icon={faCoins} className="text-[10px]" />
              ≈ {parseFloat(s.usdt_amount).toFixed(2)} USDT
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'bank_account',
      header: 'ปลายทาง',
      render: (s) =>
        s.currency === 'USDT' ? (
          <div className="flex items-center gap-2 min-w-0 max-w-[220px]">
            <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400 flex-shrink-0">
              <FontAwesomeIcon icon={faCoins} className="text-xs" />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-green-700 dark:text-green-400 text-xs">USDT ({s.network})</p>
              <p className="text-xs text-gray-500 dark:text-slate-400 truncate font-mono" title={s.wallet_address}>{s.wallet_address}</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 flex-shrink-0">
              <FontAwesomeIcon icon={faUniversity} className="text-xs" />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-gray-800 dark:text-slate-100 text-sm truncate">{s.bank_account_name}</p>
              <p className="text-xs text-gray-500 dark:text-slate-400 tabular-nums">{s.bank_account_number}</p>
              <p className="text-xs text-gray-400 dark:text-slate-500">{s.bank_name}</p>
            </div>
          </div>
        ),
    },
    {
      key: 'created_at',
      header: 'วันที่',
      width: '120px',
      sortable: true,
      render: (s) => (
        <div className="whitespace-nowrap">
          <p className="text-sm text-gray-700 dark:text-slate-200">
            {new Date(s.created_at).toLocaleDateString('th-TH')}
          </p>
          <p className="text-xs text-gray-400 dark:text-slate-500">
            {new Date(s.created_at).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '120px',
      sortable: true,
      render: (s) => (
        SETTLEMENT_STATUS_MAP[s.status]
          ? <StatusBadge status={s.status} statusMap={SETTLEMENT_STATUS_MAP} />
          : <span className="text-gray-400 dark:text-slate-500">{s.status}</span>
      ),
    },
    {
      key: 'note',
      header: 'หมายเหตุ',
      render: (s) =>
        s.note ? (
          <p className="text-xs text-gray-500 dark:text-slate-400 max-w-[160px] truncate" title={s.note}>{s.note}</p>
        ) : (
          <span className="text-gray-300 dark:text-slate-600">-</span>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '160px',
      render: (s) => (
        <div className="flex items-center gap-1 flex-wrap">
          <button
            onClick={(e) => { e.stopPropagation(); setDetailSettlement(s); setShowDetailModal(true); }}
            className="px-2 py-1 rounded-md text-[11px] font-medium bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
            title="รายละเอียด"
          >
            <FontAwesomeIcon icon={faInfoCircle} />
          </button>
          {s.slip_url && (
            <button
              onClick={(e) => { e.stopPropagation(); handleViewSlip(s.slip_url); }}
              className="px-2 py-1 rounded-md text-[11px] font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
              title="ดูสลิป"
            >
              <FontAwesomeIcon icon={faPaperclip} />
            </button>
          )}
          {s.status === 'pending' && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedSettlement(s); setShowApproveModal(true); }}
                className="px-2 py-1 rounded-md text-[11px] font-medium bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
                title="อนุมัติ"
              >
                <FontAwesomeIcon icon={faCheckCircle} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleReject(s); }}
                className="px-2 py-1 rounded-md text-[11px] font-medium bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                title="ปฏิเสธ"
              >
                <FontAwesomeIcon icon={faTimesCircle} />
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  const renderUsdtBreakdown = (s: any) => s.currency === 'USDT' && s.usdt_amount ? (
    <>
      <div className="flex justify-between text-red-600 dark:text-red-400">
        <span className="text-gray-500 dark:text-slate-400">ค่าธรรมเนียม {s.fee_percent ?? ''}%</span>
        <span className="font-medium tabular-nums">-฿{parseFloat(s.fee_amount).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
      </div>
      <div className="flex justify-between border-t border-gray-200 dark:border-slate-600 pt-2">
        <span className="text-gray-500 dark:text-slate-400">ยอดสุทธิ (THB)</span>
        <span className="font-semibold text-gray-800 dark:text-slate-100 tabular-nums">
          ฿{(parseFloat(s.amount) - parseFloat(s.fee_amount)).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-slate-400">อัตราแลกเปลี่ยน</span>
        <span className="font-medium tabular-nums">฿{parseFloat(s.usdt_rate).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / USDT</span>
      </div>
      <div className="flex justify-between items-center bg-green-50 dark:bg-green-900/20 rounded-lg px-3 py-2 border border-green-200 dark:border-green-800">
        <span className="font-semibold text-green-700 dark:text-green-400 flex items-center gap-1">
          <FontAwesomeIcon icon={faCoins} className="text-xs" /> USDT
        </span>
        <span className="font-bold text-green-700 dark:text-green-400 tabular-nums">{parseFloat(s.usdt_amount).toFixed(2)} USDT</span>
      </div>
    </>
  ) : null;

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faSackDollar}
        title="Settlement Management"
        subtitle={`จัดการคำขอถอนเงิน • ${totalItems.toLocaleString()} รายการ`}
      />

      <UiFilterPanel
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onSearch={handleSearch}
        onReset={handleReset}
        loading={loading}
        actions={
          <UiButton variant="secondary" icon={faRotate} onClick={() => { loadSettlements(); loadSummary(); }}>
            Refresh
          </UiButton>
        }
      >
        <div className="flex-1 min-w-[140px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">สถานะ</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={FILTER_SELECT_CLASS}>
            <option value="">ทั้งหมด</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div className="flex-1 min-w-[140px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">สกุลเงิน</label>
          <select value={currencyFilter} onChange={(e) => setCurrencyFilter(e.target.value)} className={FILTER_SELECT_CLASS}>
            <option value="">ทั้งหมด</option>
            <option value="THB">฿ บาท</option>
            <option value="USDT">◎ USDT</option>
          </select>
        </div>
      </UiFilterPanel>

      <UiStatsGrid cards={statCards} loading={summaryLoading} cols={5} />

      <DataTable
        data={settlements}
        columns={columns}
        loading={loading}
        emptyMessage="ไม่มีข้อมูล Settlement"
        emptyIcon={<FontAwesomeIcon icon={faSackDollar} className="text-5xl text-gray-200 dark:text-slate-600" />}
        serverPagination={{
          currentPage,
          totalItems,
          pageSize,
          onPageChange: (page) => setCurrentPage(page),
          onPageSizeChange: (size) => { setPageSize(size); setCurrentPage(1); },
        }}
        rowClassName={(s) => s.status === 'pending' ? 'bg-amber-50/40 dark:bg-amber-900/10' : ''}
        hoverable
        compact
      />

      <UiModal
        open={showApproveModal && !!selectedSettlement}
        onClose={closeApproveModal}
        title="อนุมัติการถอนเงิน"
        icon={faCheckCircle}
        iconColor="text-green-500"
        size="md"
        scrollable
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={closeApproveModal} className="flex-1 justify-center">ยกเลิก</UiButton>
            <UiButton
              icon={faUpload}
              loading={approving}
              disabled={!slipPreview}
              onClick={handleApprove}
              className="flex-1 justify-center !bg-green-600 hover:!bg-green-700"
            >
              อนุมัติและส่งสลิป
            </UiButton>
          </div>
        }
      >
        {selectedSettlement && (
          <>
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-slate-400">Merchant</span>
                <strong className="text-gray-800 dark:text-slate-100">{selectedSettlement.merchants?.name}</strong>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-slate-400">ยอดเงิน (THB)</span>
                <strong className="text-base tabular-nums" style={{ color: 'var(--theme-primary)' }}>
                  ฿{parseFloat(selectedSettlement.amount).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </strong>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500 dark:text-slate-400">ประเภท</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  selectedSettlement.currency === 'USDT'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                }`}>
                  {selectedSettlement.currency || 'THB'}
                </span>
              </div>
              {selectedSettlement.currency === 'USDT' ? (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-slate-400">Network</span>
                    <span className="font-medium text-gray-800 dark:text-slate-200">{selectedSettlement.network}</span>
                  </div>
                  {selectedSettlement.wallet_address ? (
                    <div className="flex justify-between items-start gap-2">
                      <span className="text-gray-500 dark:text-slate-400 flex-shrink-0">Address</span>
                      <code className="px-2 py-0.5 bg-white dark:bg-slate-800 rounded text-xs max-w-[200px] break-all text-right text-gray-700 dark:text-slate-300">{selectedSettlement.wallet_address}</code>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400 text-xs font-semibold">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        ยังไม่มี USDT Address — กรุณากรอกก่อน approve
                      </div>
                      <input
                        type="text"
                        value={approveWalletAddress}
                        onChange={(e) => setApproveWalletAddress(e.target.value)}
                        placeholder="TXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                        className={`${INPUT_CLASS} font-mono text-xs border-orange-300 dark:border-orange-700`}
                      />
                    </div>
                  )}
                  {renderUsdtBreakdown(selectedSettlement)}
                </>
              ) : (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-slate-400">บัญชี</span>
                    <span className="text-gray-800 dark:text-slate-200">{selectedSettlement.bank_account_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-slate-400">เลขบัญชี</span>
                    <code className="px-2 py-0.5 bg-white dark:bg-slate-800 rounded text-xs text-gray-700 dark:text-slate-300">{selectedSettlement.bank_account_number}</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-slate-400">ธนาคาร</span>
                    <span className="text-gray-800 dark:text-slate-200">{selectedSettlement.bank_name}</span>
                  </div>
                </>
              )}
            </div>

            <div>
              <label className={LABEL_CLASS}>
                <FontAwesomeIcon icon={faPaperclip} className="mr-1 normal-case" /> อัพโหลดสลิป *
              </label>
              <label className="block cursor-pointer">
                {slipPreview ? (
                  <div className="relative rounded-xl overflow-hidden border-2 border-dashed border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20">
                    <img src={slipPreview} alt="Preview" className="w-full max-h-48 object-contain" />
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <span className="text-white text-sm">คลิกเพื่อเปลี่ยนรูป</span>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-200 dark:border-slate-600 rounded-xl p-8 text-center hover:border-gray-300 dark:hover:border-slate-500 transition-colors">
                    <FontAwesomeIcon icon={faCamera} className="text-4xl text-gray-300 dark:text-slate-600 mb-2" />
                    <p className="text-gray-500 dark:text-slate-400 text-sm">อัพโหลดสลิปการโอนเงิน</p>
                  </div>
                )}
                <input type="file" accept="image/*" onChange={handleSlipChange} className="hidden" />
              </label>
            </div>

            <div>
              <label className={LABEL_CLASS}>หมายเหตุ</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className={TEXTAREA_CLASS}
                placeholder="หมายเหตุเพิ่มเติม (ถ้ามี)"
              />
            </div>
          </>
        )}
      </UiModal>

      <UiModal
        open={showDetailModal && !!detailSettlement}
        onClose={() => { setShowDetailModal(false); setDetailSettlement(null); }}
        title="รายละเอียด Settlement"
        icon={faInfoCircle}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="md"
        scrollable
        footer={
          <UiButton variant="secondary" onClick={() => { setShowDetailModal(false); setDetailSettlement(null); }} className="w-full justify-center">
            ปิด
          </UiButton>
        }
      >
        {detailSettlement && (
          <div className="space-y-4 text-sm">
            <div className="flex items-center gap-3 pb-3 border-b border-gray-100 dark:border-slate-700">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                <FontAwesomeIcon icon={faBuilding} />
              </div>
              <div>
                <p className="font-semibold text-gray-800 dark:text-slate-100">{detailSettlement.merchants?.name || '-'}</p>
                <p className="text-xs text-gray-400 dark:text-slate-500">{detailSettlement.merchants?.email}</p>
              </div>
            </div>

            <div className="space-y-2">
              <p className={LABEL_CLASS}>ยอดเงิน</p>
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-slate-400">ยอดถอน (THB)</span>
                  <span className="font-bold text-base tabular-nums" style={{ color: 'var(--theme-primary)' }}>
                    ฿{parseFloat(detailSettlement.amount).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                {detailSettlement.currency === 'USDT' && detailSettlement.usdt_amount
                  ? renderUsdtBreakdown(detailSettlement)
                  : (
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-slate-400">ประเภท</span>
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">THB</span>
                    </div>
                  )}
              </div>
            </div>

            <div className="space-y-2">
              <p className={LABEL_CLASS}>ปลายทาง</p>
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-2">
                {detailSettlement.currency === 'USDT' ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-slate-400">Network</span>
                      <span className="font-medium text-gray-800 dark:text-slate-200">{detailSettlement.network}</span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-gray-500 dark:text-slate-400">Wallet Address</span>
                      <code className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-2 text-xs break-all text-gray-700 dark:text-slate-300">{detailSettlement.wallet_address}</code>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-slate-400">ชื่อบัญชี</span>
                      <span className="font-medium text-gray-800 dark:text-slate-200">{detailSettlement.bank_account_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-slate-400">เลขบัญชี</span>
                      <code className="px-2 py-0.5 bg-white dark:bg-slate-800 rounded text-xs border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-300">{detailSettlement.bank_account_number}</code>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-slate-400">ธนาคาร</span>
                      <span className="font-medium text-gray-800 dark:text-slate-200">{detailSettlement.bank_name}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <p className={LABEL_CLASS}>สถานะ</p>
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 dark:text-slate-400">สถานะ</span>
                  {SETTLEMENT_STATUS_MAP[detailSettlement.status]
                    ? <StatusBadge status={detailSettlement.status} statusMap={SETTLEMENT_STATUS_MAP} />
                    : <span>{detailSettlement.status}</span>}
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-slate-400">วันที่สร้าง</span>
                  <span className="font-medium text-gray-800 dark:text-slate-200">{new Date(detailSettlement.created_at).toLocaleString('th-TH')}</span>
                </div>
                {detailSettlement.approved_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-slate-400">วันที่อนุมัติ</span>
                    <span className="font-medium text-gray-800 dark:text-slate-200">{new Date(detailSettlement.approved_at).toLocaleString('th-TH')}</span>
                  </div>
                )}
                {detailSettlement.approved_by && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-slate-400">อนุมัติโดย</span>
                    <span className="font-medium text-xs text-gray-800 dark:text-slate-200">{detailSettlement.approved_by}</span>
                  </div>
                )}
                {detailSettlement.note && (
                  <div className="pt-2 border-t border-gray-200 dark:border-slate-600">
                    <span className="text-gray-500 dark:text-slate-400">หมายเหตุ</span>
                    <p className="mt-1 text-gray-700 dark:text-slate-300 bg-white dark:bg-slate-800 rounded-lg p-2 border border-gray-200 dark:border-slate-600">{detailSettlement.note}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </UiModal>

      {showSlipModal && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowSlipModal(false)}
        >
          <div className="relative max-w-3xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowSlipModal(false)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-white dark:bg-slate-700 text-gray-600 dark:text-slate-300 rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 dark:hover:bg-slate-600 z-10"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            {selectedSlipUrl ? (
              <img
                src={selectedSlipUrl}
                alt="Slip"
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
                onError={() => toast.error('ไม่สามารถโหลดรูปภาพสลิปได้')}
              />
            ) : (
              <p className="text-gray-400 text-center py-8">ไม่พบข้อมูลสลิป</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SettlementManagementPage;
